import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Headphones, History, Loader2, MessageSquarePlus, Send, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { klarysUrl, lireTexte, streamKlarys, visibleText } from './klarysApi';
import { KlarysEtape, KlarysMessage } from './KlarysMessage';
import { KlarysActionCard } from './KlarysActionCard';
import { KlarysMic } from './KlarysMic';
import { KlarysHistorique, useHistoriqueKlarys } from './KlarysHistorique';
import { KlarysFavorisBar, useFavoris } from './KlarysFavoris';
import { KlarysPartageDialog } from './KlarysPartageDialog';
import { analyserCommande } from './commandesVocales';
import { KlarysModeleDialog, KlarysModeleForm, KlarysModelesBar, useModeles, variablesDe } from './KlarysModeles';
import { BoutonJoindre, fichiersColles, telechargerExemples, KlarysDocumentCard, KlarysPiecesEnAttente, KlarysPiecesMessage, usePiecesJointes } from './KlarysPieces';

const EXEMPLES = [
  'Liste les impayés RCDS en cours',
  'Combien de devis à 0 € reste-t-il à nettoyer ?',
  'Quels sont les encaissements CB de ce mois ?',
  'Reporte de 15 jours les échéances restantes du contrat …',
];

const toItems = (messages, actions) => {
  const parId = Object.fromEntries(actions.map((a) => [a.id, a]));
  return messages.flatMap((m) => {
    if (m.role === 'tool' && m.meta?.document) return [{ k: 'document', f: m.meta.document }];
    if (m.role === 'tool') return m.meta?.action_id && parId[m.meta.action_id] ? [{ k: 'action', action: parId[m.meta.action_id] }] : [];
    const text = m.role === 'assistant' ? visibleText(m.content) : m.content;
    const out = text ? [{ k: 'msg', role: m.role, text, dictee: !!m.meta?.dictee, id: m.id }] : [];
    return m.meta?.fichiers?.length ? [...out, { k: 'pieces', fichiers: m.meta.fichiers }] : out;
  });
};

const ConvList = ({ convs, current, onSelect, onNew, onDelete }) => (
  <aside className="hidden md:flex w-52 shrink-0 flex-col border-r border-slate-200 bg-slate-50">
    <button type="button" onClick={onNew} className="m-2 inline-flex items-center gap-1.5 rounded-lg bg-[#0a3d62] px-3 py-2 text-xs font-semibold text-white hover:bg-[#0c4a77]" data-testid="klarys-new-conv">
      <MessageSquarePlus className="h-4 w-4" /> Nouvelle conversation
    </button>
    <ul className="flex-1 overflow-y-auto px-1 pb-2">
      {convs.map((c) => (
        <li key={c.id} className={`group flex items-center rounded-md text-xs ${c.id === current ? 'bg-white shadow-sm' : 'hover:bg-white/70'}`}>
          <button type="button" onClick={() => onSelect(c.id)} className="flex-1 truncate px-2 py-2 text-left text-slate-700" data-testid={`klarys-conv-${c.id}`}>{c.titre}</button>
          <button type="button" onClick={() => onDelete(c.id)} className="px-1.5 text-slate-300 hover:text-rose-600" data-testid={`klarys-conv-delete-${c.id}`} aria-label="Supprimer">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ul>
  </aside>
);

export const KlarysChat = ({ headers, cible, demandeExterne, onDemandeTraitee }) => {
  const [convs, setConvs] = useState([]);
  const [current, setCurrent] = useState(null);
  const [items, setItems] = useState([]);
  const [texte, setTexte] = useState('');
  const [dictee, setDictee] = useState(false);
  const [corrigeDe, setCorrigeDe] = useState(null);
  const [histOuvert, setHistOuvert] = useState(false);
  const saisie = useRef(null);
  const hist = useHistoriqueKlarys(headers);
  const fav = useFavoris(headers);
  const [aPartager, setAPartager] = useState(null);
  const pj = usePiecesJointes(headers);
  const mod = useModeles(headers);
  const [modeleEdit, setModeleEdit] = useState(null);
  const [modeleForm, setModeleForm] = useState(null);
  const [partageType, setPartageType] = useState(null);
  const [survol, setSurvol] = useState(false);
  const reprendre = (t, fromId = null, estDictee = false) => {
    setTexte(t);
    setCorrigeDe(fromId);
    setDictee(estDictee);
    setHistOuvert(false);
    hist.reinitialiser();
    setTimeout(() => { const el = saisie.current; if (el) { el.focus(); el.setSelectionRange(t.length, t.length); } }, 0);
  };
  const [busy, setBusy] = useState(false);
  const etaitOccupe = useRef(false);
  useEffect(() => {
    if (!texte && saisie.current) saisie.current.style.height = 'auto';
  }, [texte]);
  useEffect(() => {
    if (etaitOccupe.current && !busy) window.dispatchEvent(new Event('klarys-reponse-prete'));
    etaitOccupe.current = busy;
  }, [busy]);
  const [mainsLibres, setMainsLibres] = useState(false);
  const [ecoute, setEcoute] = useState(0);
  const bas = useRef(null);
  const derniere = useRef('');

  const loadConvs = useCallback(() => axios.get(klarysUrl('/conversations'), { headers }).then((r) => { setConvs(r.data.items); return r.data.items; }), []); // eslint-disable-line react-hooks/exhaustive-deps
  const open = useCallback(async (id) => {
    setCurrent(id);
    const r = await axios.get(klarysUrl(`/conversations/${id}`), { headers });
    setItems(toItems(r.data.messages, r.data.actions));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (demandeExterne?.texte) { loadConvs(); return; }
    loadConvs().then((l) => { if (cible) open(cible); else if (l[0]) open(l[0].id); });
  }, [cible]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { bas.current?.scrollIntoView({ behavior: 'smooth' }); }, [items]);

  const creation = useRef(null);
  const nouvelle = () => {
    if (!creation.current) {
      creation.current = (async () => {
        const r = await axios.post(klarysUrl('/conversations'), {}, { headers });
        setCurrent(r.data.id);
        setItems([]);
        loadConvs();
        return r.data.id;
      })().finally(() => { creation.current = null; });
    }
    return creation.current;
  };

  const onEvent = (e) => setItems((prev) => {
    const next = [...prev];
    const last = next[next.length - 1];
    if (e.type === 'delta') {
      derniere.current += e.texte;
      if (last?.k === 'msg' && last.role === 'assistant' && last.live) next[next.length - 1] = { ...last, text: last.text + e.texte };
      else next.push({ k: 'msg', role: 'assistant', text: e.texte, live: true });
    } else if (e.type === 'etape') {
      next.push({ k: 'etape', label: e.label });
    } else if (e.type === 'attente') {
      next.push({ k: 'action', action: e.action });
    } else if (e.type === 'action') {
      return next.map((it) => (it.k === 'action' && it.action.id === e.id ? { ...it, action: { ...it.action, statut: e.statut, resultat_statut: e.http } } : it));
    } else if (e.type === 'document') {
      next.push({ k: 'document', f: e.fichier });
    } else if (e.type === 'erreur') {
      next.push({ k: 'msg', role: 'assistant', text: `⚠️ ${e.message}` });
    }
    return next.map((it) => (e.type === 'fin' && it.live ? { ...it, live: false } : it));
  });

  const run = async (path, body) => {
    setBusy(true);
    derniere.current = '';
    try { await streamKlarys(path, headers, body, onEvent); } catch (err) { toast.error(err.message); } finally { setBusy(false); loadConvs(); }
    if (mainsLibresRef.current && derniere.current.trim()) {
      try { await lireTexte(derniere.current, headers); } catch (err) { toast.error(err.message); }
      if (mainsLibresRef.current) setEcoute((n) => n + 1);
    }
  };
  const mainsLibresRef = useRef(false);
  useEffect(() => { mainsLibresRef.current = mainsLibres; }, [mainsLibres]);

  const envoyer = async (t = texte, viaDictee = dictee, analyser = true, corrigeId = null) => {
    let msg = t.trim();
    const joints = pj.prets();
    if (pj.enCours) { toast.info('Patientez : analyse des fichiers en cours'); return; }
    if (!msg && joints.length) msg = 'Analyse les fichiers joints et fais-m\'en une synthèse.';
    if (!msg || busy) return;
    let corrige = corrigeId || corrigeDe;
    const cmd = analyser ? analyserCommande(msg, { derniere: hist.items[0], favoris: fav.items }) : null;
    if (cmd?.type === 'erreur') { toast.error(cmd.message); return; }
    if (cmd) {
      msg = cmd.texte;
      if (cmd.type === 'corrige') corrige = cmd.corrigeDe;
      toast.info(cmd.type === 'refais' ? 'Dernière demande relancée' : cmd.type === 'corrige' ? 'Demande corrigée et renvoyée' : 'Favori lancé', { description: msg.slice(0, 120) });
    }
    const id = joints[0]?.conv || current || await nouvelle();
    setTexte('');
    setItems((prev) => [...prev.map((it) => (it.k === 'action' && it.action.statut === 'en_attente' ? { ...it, action: { ...it.action, statut: 'annulee' } } : it)), { k: 'msg', role: 'user', text: msg, dictee: viaDictee },
      ...(joints.length ? [{ k: 'pieces', fichiers: joints }] : [])]);
    const corps = { texte: msg, dictee: viaDictee, ...(corrige ? { corrige_de: corrige } : {}), ...(joints.length ? { fichiers: joints.map((j) => j.id) } : {}) };
    pj.vider();
    setDictee(false);
    setCorrigeDe(null);
    hist.reinitialiser();
    await run(`/conversations/${id}/message`, corps);
    hist.charger();
  };
  useEffect(() => {
    if (!demandeExterne?.texte) return;
    onDemandeTraitee?.();
    if (demandeExterne.gedId) {
      (async () => {
        try {
          creation.current = null;
          const id = await nouvelle();
          toast.info(`Lecture de « ${demandeExterne.nomDoc} » par Klarys…`);
          const r = await axios.post(klarysUrl(`/conversations/${id}/joindre-ged`), { ged_id: demandeExterne.gedId }, { headers });
          const j = { ...r.data, conv: id };
          setItems([{ k: 'msg', role: 'user', text: demandeExterne.texte }, { k: 'pieces', fichiers: [j] }]);
          run(`/conversations/${id}/message`, { texte: demandeExterne.texte, fichiers: [j.id] });
        } catch (e) { toast.error(e.response?.data?.detail || 'Document impossible à joindre'); }
      })();
      return;
    }
    if (demandeExterne.modeleTitre && variablesDe(demandeExterne.texte).length) setModeleForm({ titre: demandeExterne.modeleTitre, texte: demandeExterne.texte });
    else envoyer(demandeExterne.texte, false, false);
  }, [demandeExterne]); // eslint-disable-line react-hooks/exhaustive-deps

  const toucheSaisie = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); envoyer(); return; }
    const el = e.currentTarget;
    const enHaut = !el.value.slice(0, el.selectionStart).includes('\n');
    const enBas = !el.value.slice(el.selectionEnd).includes('\n');
    if ((e.key === 'ArrowUp' && enHaut) || (e.key === 'ArrowDown' && enBas)) {
      const t = hist.naviguer(e.key === 'ArrowUp' ? 1 : -1, el.value);
      if (t !== null) { e.preventDefault(); setTexte(t); setCorrigeDe(null); }
    } else if (e.key === 'Escape' && histOuvert) setHistOuvert(false);
  };

  const confirmer = (a, corps) => {
    setItems((prev) => prev.map((it) => (it.k === 'action' && it.action.id === a.id ? { ...it, action: { ...it.action, statut: 'en_cours', exclus: corps?.exclus } } : it)));
    run(`/actions/${a.id}/confirmer`, corps).then(() => window.dispatchEvent(new Event('klarys-compteurs')));
  };
  const annuler = async (a) => {
    await axios.post(klarysUrl(`/actions/${a.id}/annuler`), {}, { headers });
    window.dispatchEvent(new Event('klarys-compteurs'));
    setItems((prev) => prev.map((it) => (it.k === 'action' && it.action.id === a.id ? { ...it, action: { ...it.action, statut: 'annulee' } } : it)));
  };
  const donnerAvis = async (it, note, commentaire) => {
    try {
      const r = await axios.post(klarysUrl(`/conversations/${current}/avis`), { note, commentaire, extrait: it.text.slice(0, 600), message_id: it.id || '' }, { headers });
      if (r.data.memoire) toast.success('Correction mémorisée : Klarys l\'appliquera désormais');
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || 'Avis non enregistré'); return false; }
  };
  const supprimer = async (id) => {
    await axios.delete(klarysUrl(`/conversations/${id}`), { headers });
    const l = await loadConvs();
    if (id === current) { setItems([]); setCurrent(null); if (l[0]) open(l[0].id); }
  };

  return (
    <div className="flex h-full min-h-0">
      <ConvList convs={convs} current={current} onSelect={(id) => { pj.vider(); open(id); }} onNew={() => { pj.vider(); nouvelle(); }} onDelete={supprimer} />
      <div className="flex min-w-0 flex-1 flex-col bg-[#f6f8fb]">
        <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="klarys-messages">
          {!items.length && (
            <div className="mt-8 space-y-3 text-center" data-testid="klarys-empty">
              <p className="text-sm text-slate-600">Demandez à Klarys d'exécuter une tâche d'administration.</p>
              <div className="flex flex-wrap justify-center gap-2">
                {EXEMPLES.map((ex, i) => (
                  <button key={ex} type="button" onClick={() => setTexte(ex)} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 hover:border-[#0a3d62]" data-testid={`klarys-exemple-${i}`}>{ex}</button>
                ))}
              </div>
            </div>
          )}
          {items.map((it, i) => {
            if (it.k === 'etape') return <KlarysEtape key={i} label={it.label} testId={`klarys-etape-${i}`} />;
            if (it.k === 'document') return <KlarysDocumentCard key={`doc-${it.f.id}`} f={it.f} headers={headers} testId={`klarys-document-${i}`} />;
            if (it.k === 'pieces') return <KlarysPiecesMessage key={`pj-${i}`} fichiers={it.fichiers} headers={headers} testId={`klarys-msg-pieces-${i}`} />;
            if (it.k === 'action') return <KlarysActionCard key={it.action.id} action={it.action} onConfirm={confirmer} onCancel={annuler} disabled={busy} />;
            return <KlarysMessage key={i} role={it.role} text={it.text} dictee={it.dictee} testId={`klarys-msg-${i}`} headers={it.live ? null : headers}
              busy={busy} onRenvoyer={it.role === 'user' ? (t) => envoyer(t, false, false, it.id) : null}
              estFavori={it.role === 'user' && fav.estFavori(it.text)} onFavori={it.role === 'user' ? () => fav.ajouter(it.text) : null}
              onPartager={it.role === 'user' ? () => { setPartageType(null); setAPartager(it.text); } : null}
              onModele={it.role === 'user' ? () => setModeleEdit({ texte: it.text }) : null}
              onAvis={it.role === 'assistant' && current ? (note, commentaire) => donnerAvis(it, note, commentaire) : null} />;
          })}
          {busy && <div className="ml-9 flex items-center gap-2 text-xs text-slate-500" data-testid="klarys-busy"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Klarys travaille…</div>}
          <div ref={bas} />
        </div>
        <div className={`border-t bg-white p-3 ${survol ? 'border-[#0a3d62] bg-sky-50' : 'border-slate-200'}`} data-testid="klarys-zone-saisie"
          onDragOver={(e) => { e.preventDefault(); setSurvol(true); }} onDragLeave={() => setSurvol(false)}
          onDrop={(e) => { e.preventDefault(); setSurvol(false); if (!busy) pj.ajouter(e.dataTransfer.files, () => creation.current || current || nouvelle()); }}>
          <KlarysPiecesEnAttente pj={pj} />
          <KlarysFavorisBar fav={fav} disabled={busy} onLancer={(t) => envoyer(t, false, false)} />
          <KlarysModelesBar mod={mod} disabled={busy} onRemplir={setModeleForm} onEditer={setModeleEdit}
            onPartager={(m) => { setPartageType({ type: 'modele', titre: m.titre }); setAPartager(m.texte); }} />
          {aPartager && <KlarysPartageDialog texte={aPartager} headers={headers} type={partageType?.type} titre={partageType?.titre} onClose={() => { setAPartager(null); setPartageType(null); }} />}
          {modeleEdit && <KlarysModeleDialog initial={modeleEdit} onClose={() => setModeleEdit(null)} onSave={mod.enregistrer} />}
          {modeleForm && <KlarysModeleForm modele={modeleForm} onClose={() => setModeleForm(null)} onEnvoyer={(t) => envoyer(t, false, false)} />}
          {corrigeDe && (
            <p className="mb-1.5 flex items-center gap-2 text-[11px] text-amber-700" data-testid="klarys-correction-bandeau">
              Correction d'une demande précédente : elle sera renvoyée comme une nouvelle demande.
              <button type="button" onClick={() => { setCorrigeDe(null); setTexte(''); }} className="underline" data-testid="klarys-correction-annuler">Annuler</button>
            </p>
          )}
          <div className="relative flex items-end gap-2">
            {histOuvert && <KlarysHistorique charger={hist.charger} onChoisir={(h) => reprendre(h.texte, h.id, h.dictee)} onFermer={() => setHistOuvert(false)}
              onFavori={(h) => fav.ajouter(h.texte)} onPartager={(h) => setAPartager(h.texte)} />}
            <button type="button" onClick={() => setHistOuvert((o) => !o)} title="Historique des 100 dernières demandes (↑ / ↓ dans la saisie)"
              aria-label="Historique des demandes" data-testid="klarys-historique-btn"
              className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border ${histOuvert ? 'border-[#0a3d62] text-[#0a3d62]' : 'border-slate-300 text-slate-600'} hover:border-[#0a3d62]`}>
              <History className="h-4 w-4" />
            </button>
            <BoutonJoindre disabled={busy} onFichiers={(f) => pj.ajouter(f, () => creation.current || current || nouvelle())} />
            <Textarea ref={saisie} value={texte} onChange={(e) => { setTexte(e.target.value); if (!e.target.value) setDictee(false); }} rows={2}
              placeholder="Votre demande — Maj+Entrée pour aller à la ligne ; glissez un fichier ou collez une capture (Ctrl+V) ; ↑ historique"
              onKeyDown={toucheSaisie}
              onPaste={(e) => { const f = fichiersColles(e); if (f && busy) toast.info('Patientez : Klarys travaille'); else if (f) pj.ajouter(f, () => creation.current || current || nouvelle()); }}
              style={{ height: 'auto', maxHeight: 220 }}
              onInput={(e) => { const el = e.currentTarget; el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 220)}px`; }}
              className="min-h-[44px] min-w-[180px] flex-1 resize-none overflow-y-auto text-sm" data-testid="klarys-input" />
            <KlarysMic headers={headers} disabled={busy} declencheur={mainsLibres ? ecoute : 0}
              onTexte={(t) => { if (mainsLibresRef.current) { envoyer(t, true); return; } setDictee(true); setTexte((prev) => (prev.trim() ? `${prev.trim()} ${t}` : t)); }} />
            <Button onClick={() => envoyer()} disabled={busy || pj.enCours || (!texte.trim() && !pj.prets().length)} className="h-11 bg-[#0a3d62] hover:bg-[#0c4a77]" data-testid="klarys-send-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className="mt-2 flex flex-wrap items-center gap-x-1.5 text-[11px] text-slate-500" data-testid="klarys-formats-acceptes">
            Fichiers acceptés : PDF, Word, texte, JPG/PNG/WEBP, Excel, CSV, ZIP (10 × 25 Mo), privés et relus dans la suite de la discussion.
            <button type="button" onClick={() => telechargerExemples(headers)} className="font-semibold text-[#0a3d62] underline-offset-2 hover:underline" data-testid="klarys-exemples-zip-btn">
              Télécharger des exemples (ZIP)
            </button>
          </p>
          <label className="mt-1 inline-flex cursor-pointer items-center gap-1.5 text-[11px] text-slate-600" data-testid="klarys-mains-libres">
            <input type="checkbox" checked={mainsLibres} onChange={(e) => { setMainsLibres(e.target.checked); if (e.target.checked) setEcoute((n) => n + 1); }}
              data-testid="klarys-mains-libres-toggle" className="accent-[#0a3d62]" />
            <Headphones className="h-3.5 w-3.5" /> Mode mains libres (réponses lues à voix haute, micro relancé automatiquement)
          </label>
          <p className="mt-1.5 text-[10px] text-slate-400">Toute modification ou tout envoi vous est soumis pour confirmation. Les actions sont tracées dans le journal.</p>
        </div>
      </div>
    </div>
  );
};
